import type { CarrierBid, CarrierBidQuote } from './types.js';

/**
 * @aiPurpose pure helper: aggregate the request's product quantity (SKU
 *   count) by summing `items[].productQuantity`. Returns `null` if any
 *   item carries a null productQuantity (legacy rows, CONTAINER), since
 *   a partial sum would understate the true basis for a `'piece'` bid.
 *   Empty input → `null` so callers fall back to per-unit display.
 * @aiInvariant any single null contaminates the aggregate — the UI must
 *   not invent a basis. Caller decides whether to render per-unit only
 *   or to omit the bid from comparison ranking.
 */
export function aggregateProductQuantity(
  items: ReadonlyArray<{ productQuantity: number | null }>,
): number | null {
  if (items.length === 0) return null;
  let sum = 0;
  for (const it of items) {
    if (it.productQuantity === null) return null;
    sum += it.productQuantity;
  }
  return sum;
}

/**
 * @aiPurpose compute the native + FX-adjusted total for one bid against
 *   a request's `weight` / `volume` / `productQuantity`. `total` stays
 *   in the bid currency (`pricePerUnit * basis`, where basis = weight
 *   (kg) / volume (m3) / productQuantity (piece)). `usdHint` is the
 *   USD-equivalent of that total, derived from the CBR snapshot as
 *   `total * rates[currency] / rates.USD`. The hint is omitted
 *   (`undefined`) when the bid currency is USD (no conversion needed)
 *   or when either rate is missing/zero so callers do not show a stale
 *   or misleading hint.
 * @aiInvariant returns `null` when the request's aggregate basis is
 *   unknown (`null`); callers decide whether to hide the total or fall
 *   back to per-unit display. `rubEquivalent` is used for cross-currency
 *   comparison (cheapest selection); it falls back to `total` when the
 *   currency's rate is unavailable so a missing snapshot doesn't crash
 *   the ranker.
 * @aiInvariant `'piece'` bids (#1060) use `request.productQuantity` as
 *   basis. When the aggregate productQuantity is null (any item missing
 *   the SKU count), the helper returns `null` so the UI renders only
 *   the per-piece price and `getCheapestBid` skips the bid. This avoids
 *   inventing a total when the request data is incomplete.
 */
export function computeBidTotal(
  bid: CarrierBidQuote,
  request: { weight: number | null; volume: number | null; productQuantity?: number | null },
  fxRates: Record<string, number>,
): { total: number; rubEquivalent: number; usdHint: number | undefined } | null {
  const basis =
    bid.unit === 'kg'
      ? request.weight
      : bid.unit === 'm3'
        ? request.volume
        : (request.productQuantity ?? null);
  if (basis === null) return null;
  const total = bid.pricePerUnit * basis;
  const currencyRate = fxRates[bid.currency];
  const usdRate = fxRates.USD;
  const rubEquivalent = currencyRate && currencyRate > 0 ? total * currencyRate : total;
  const canHintUsd =
    bid.currency !== 'USD' &&
    typeof currencyRate === 'number' &&
    currencyRate > 0 &&
    typeof usdRate === 'number' &&
    usdRate > 0;
  const usdHint = canHintUsd ? (total * currencyRate) / usdRate : undefined;
  return { total, rubEquivalent, usdHint };
}

/**
 * @aiPurpose pure FX-aware "cheapest bid" selector across the carrier
 *   list. Only primary (`scope === 'REQUEST'`) bids compete: overrides
 *   (ITEM / BOX / BOX_ITEM / BOX_GROUP) cover a sub-resource and would
 *   distort the request-level ranking. Walks every primary
 *   `CarrierBidQuote` on every entry and ranks by `rubEquivalent`. Ties
 *   resolve by first occurrence (stable preference).
 * @aiInvariant Entries with no primary bid are skipped. Quotes whose
 *   unit basis is unknown on the request (`weight === null` for 'kg',
 *   `volume === null` for 'm3', `productQuantity === null` for 'piece')
 *   are skipped — they cannot be scored. Empty input → `null`. Returns
 *   the carrier-entry AND the winning primary bid so call sites can
 *   highlight either dimension.
 */
export function getCheapestBid(
  carriers: CarrierBid[],
  request: { weight: number | null; volume: number | null; productQuantity?: number | null },
  fxRates: Record<string, number>,
): { carrier: CarrierBid; bid: CarrierBidQuote } | null {
  let best: { carrier: CarrierBid; bid: CarrierBidQuote } | null = null;
  let bestScore = Infinity;
  for (const carrier of carriers) {
    for (const bid of carrier.bids) {
      if (bid.scope !== 'REQUEST') continue;
      const totals = computeBidTotal(bid, request, fxRates);
      if (totals === null) continue;
      if (totals.rubEquivalent < bestScore) {
        best = { carrier, bid };
        bestScore = totals.rubEquivalent;
      }
    }
  }
  return best;
}

/**
 * @aiPurpose for a given sub-resource key, return the most specific bid
 *   on the carrier that covers it — preferring overrides matching the
 *   exact `(scope, scopeRefId)` over the carrier's primary
 *   (`scope = REQUEST`) bid. Used by the UI to render the effective
 *   price on per-item and per-box rows while still falling back to the
 *   primary when no override is in place.
 * @aiInvariant Returns the override bid when an exact match is found;
 *   otherwise the primary bid; otherwise `null`.
 */
export function getEffectiveBidFor(
  bids: ReadonlyArray<CarrierBidQuote>,
  ref: { scope: 'ITEM' | 'BOX' | 'BOX_ITEM' | 'BOX_GROUP'; scopeRefId: string },
): CarrierBidQuote | null {
  let primary: CarrierBidQuote | null = null;
  for (const bid of bids) {
    if (bid.scope === ref.scope && bid.scopeRefId === ref.scopeRefId) return bid;
    if (bid.scope === 'REQUEST' && primary === null) primary = bid;
  }
  return primary;
}
